import BaseEntity from 'src/entities/BaseEntity';
import { EntityDescriptions } from 'src/store/entities/types';
import EntityDescription from 'src/store/entities/types/EntityDescription';
import { DiscountEntry, MinQuantity, PremiumLevelDiscounts, sortMinQuantity } from './Discounts';

export default class VolumeDiscount extends BaseEntity {
    public get orderedMinQuantitySet(): MinQuantity[] {
        const minQuantityMap = new Map(
            this.premiumLevelDiscounts.flatMap(({ orderedDiscountEntries }) =>
                orderedDiscountEntries.map(ode => [ode.minQuantity, ode])
            )
        );
        return Array.from(minQuantityMap.values()).sort(sortMinQuantity);
    }

    public get activePremiumLevelDiscounts() {
        return this.getActivePremiumLevelDiscount().orderedDiscountEntries;
    }

    public get activePremiumLevelMaxDiscount() {
        return this.getActivePremiumLevelDiscount().maxDiscountEntry;
    }

    public get baseColli() {
        return this.activePremiumLevelDiscounts?.[0]?.colli ?? '';
    }

    public get premiumLevelCount(): number {
        return this.premiumLevelDiscounts.length;
    }
    public readonly articleGroupNormalizedName: string;
    public readonly articleGroupLocalizedName: string;
    public readonly premiumLevelDiscounts: PremiumLevelDiscounts[];
    public readonly activePremiumLevel: number;

    constructor(json: any) {
        super({ id: json.articleGroupNormalizedName, ...json });

        this.articleGroupNormalizedName = json.articleGroupNormalizedName;
        this.articleGroupLocalizedName = json.articleGroupLocalizedName;

        this.premiumLevelDiscounts = json.premiumLevelDiscounts
            .sort((a: any, b) => a.premiumLevel - b.premiumLevel)
            .map((pld: any) => new PremiumLevelDiscounts(pld));

        this.activePremiumLevel = json.activePremiumLevel;
    }

    public getDiscounts(minQuantity: number) {
        return this.premiumLevelDiscounts.map(pld => pld.getDiscount(minQuantity));
    }

    /**
     * Returns the appropriate discount for the given quantity
     */
    public getDiscountByQuantity(quantity: number): DiscountEntry | undefined {
        let discount: DiscountEntry | undefined;
        this.activePremiumLevelDiscounts.forEach(d => {
            if (quantity >= d.minQuantity) discount = d;
        });
        return discount;
    }

    public getNextApplicableDiscount(quantity: number): DiscountEntry | undefined {
        let discount: DiscountEntry | undefined;
        this.activePremiumLevelDiscounts.reverse().forEach(d => {
            if (quantity < d.minQuantity) discount = d;
        });
        return discount;
    }

    getEntityDescription(): EntityDescription {
        return EntityDescriptions.VOLUME_DISCOUNT;
    }

    toString(): string {
        return `${this.articleGroupNormalizedName}:${this.articleGroupNormalizedName}`;
    }

    private getActivePremiumLevelDiscount() {
        return this.premiumLevelDiscounts.filter(pld => pld.premiumLevel === this.activePremiumLevel)[0];
    }
}
